import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    
    // On initial load, ensure featured image is shown (not variant image)
    // Check if URL has variant parameter - if not, it's initial load
    const urlParams = new URLSearchParams(window.location.search);
    const hasVariantParam = urlParams.has('variant') || urlParams.has('option_values');
    
    if (!hasVariantParam) {
      // Wait for slideshow to initialize, then select featured image
      // Use multiple attempts to ensure slideshow is ready
      let attempts = 0;
      const maxAttempts = 20;
      
      const selectFeaturedImage = () => {
        const slideshow = this.slideshow;
        if (!slideshow?.refs) {
          if (attempts < maxAttempts) {
            attempts++;
            requestAnimationFrame(selectFeaturedImage);
          }
          return;
        }
        
        const slides = slideshow.refs.slides;
        if (!slides || slides.length === 0) {
          if (attempts < maxAttempts) {
            attempts++;
            requestAnimationFrame(selectFeaturedImage);
          }
          return;
        }
        
        // Find the slide with product featured media
        const productFeaturedMediaId = this.dataset.productFeaturedMediaId;
        
        if (productFeaturedMediaId) {
          // Try to find slide with featured media ID
          for (const slide of slides) {
            if (!slide) continue;
            const mediaElement = slide.querySelector('[data-media-id]');
            const slideMediaId = mediaElement?.getAttribute('data-media-id');
            if (slideMediaId === productFeaturedMediaId) {
              const slideId = slide.getAttribute('slide-id');
              if (slideId) {
                slideshow.select({ id: slideId }, undefined, { animate: false });
                return;
              }
            }
          }
        }
        
        // Fallback: select first slide (should be featured image after Liquid sorting)
        const firstSlide = slides[0];
        if (firstSlide) {
          const firstSlideId = firstSlide.getAttribute('slide-id');
          if (firstSlideId) {
            slideshow.select({ id: firstSlideId }, undefined, { animate: false });
          }
        }
      };
      
      // Start trying after a short delay to ensure DOM is ready
      requestAnimationFrame(selectFeaturedImage);
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Get the variant's featured media ID from the event
    const variantMediaId = event.detail.resource?.featured_media?.id;
    
    // Store reference to this gallery's parent before replacement
    const parentElement = this.parentElement;
    
    this.replaceWith(newMediaGallery);
    
    // After replacing, ensure the variant image is selected if variant has a featured media
    // Use multiple animation frames to ensure the slideshow is fully initialized
    if (variantMediaId) {
      // Wait for the new gallery to be connected and slideshow to initialize
      const selectVariantImage = () => {
        // Find the gallery that was just inserted (should be in the same parent)
        const updatedGalleryElement = parentElement?.querySelector('media-gallery');
        if (!updatedGalleryElement || !(updatedGalleryElement instanceof MediaGallery)) return false;
        
        const updatedGallery = /** @type {MediaGallery} */ (updatedGalleryElement);
        const slideshow = updatedGallery.slideshow;
        if (!slideshow) return false;
        
        const slides = slideshow.refs?.slides || [];
        if (slides.length === 0) return false;
        
        // First, try to find the slide containing the media with the variant's media ID
        for (const slide of slides) {
          if (!slide) continue;
          const mediaElement = slide.querySelector('[data-media-id]');
          const slideMediaId = mediaElement?.getAttribute('data-media-id');
          if (slideMediaId === String(variantMediaId)) {
            const slideId = slide.getAttribute('slide-id');
            if (slideId) {
              slideshow.select({ id: slideId }, undefined, { animate: false });
              return true;
            }
          }
        }
        
        // If not found by media ID, select the first slide
        // Liquid template should have sorted media to put variant image first
        if (slides[0]) {
          const firstSlide = slides[0];
          const firstSlideId = firstSlide.getAttribute('slide-id');
          if (firstSlideId) {
            slideshow.select({ id: firstSlideId }, undefined, { animate: false });
            return true;
          }
        }
        
        return false;
      };
      
      // Try multiple times with increasing delays to ensure slideshow is ready
      let attempts = 0;
      const maxAttempts = 15;
      const trySelect = () => {
        if (selectVariantImage() || attempts >= maxAttempts) {
          return;
        }
        attempts++;
        requestAnimationFrame(trySelect);
      };
      
      requestAnimationFrame(trySelect);
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
